// ------------------------------------------------------------
// Input rate unit helpers
// Engine uses generation_profile for rates; products_per_minute is the
// legacy/display fallback when profile is missing or default (1.0/min).
// ------------------------------------------------------------

export const QUANTITY_UNIT_PRESETS = [
  'kg',
  'items',
  'units',
  'batches',
  'tons',
  'liters',
] as const;

const RATE_UNIT_PATTERN = /^(.+)\/(min|minute|hr|hour)$/i;

export function looksLikeRateUnit(inputUnit: string): boolean {
  return RATE_UNIT_PATTERN.test(inputUnit.trim());
}

export function quantityUnitFromInputUnit(inputUnit: string): string {
  const trimmed = inputUnit.trim();
  if (!trimmed) return 'kg';

  const match = trimmed.match(RATE_UNIT_PATTERN);
  if (match) return match[1].trim();

  if (/^(kg|items?|units?|batches?|tons?|liters?)$/i.test(trimmed)) {
    return trimmed;
  }

  return 'kg';
}

export function formatInputUnit(quantityUnit: string): string {
  const qty = quantityUnit.trim() || 'kg';
  return `${qty}/min`;
}

export function formatIncomingRate(
  ratePerMinute: number,
  inputUnit: string,
): string {
  const qty = quantityUnitFromInputUnit(inputUnit);
  const rounded =
    Math.abs(ratePerMinute - Math.round(ratePerMinute)) < 0.05
      ? Math.round(ratePerMinute)
      : Math.round(ratePerMinute * 10) / 10;
  return `${rounded} ${qty}/min`;
}

export function formatHourlyEquivalent(
  ratePerMinute: number,
  inputUnit: string,
): string {
  const qty = quantityUnitFromInputUnit(inputUnit);
  const perHour = ratePerMinute * 60;
  const rounded =
    Math.abs(perHour - Math.round(perHour)) < 0.5
      ? Math.round(perHour)
      : Math.round(perHour * 10) / 10;
  return `${rounded} ${qty}/hour`;
}

export function isMassFlowQuantityUnit(quantityUnit: string): boolean {
  const q = quantityUnit.trim().toLowerCase();
  return (
    q === 'kg' ||
    q === 'kilogram' ||
    q === 'kilograms' ||
    q === 'ton' ||
    q === 'tons' ||
    q === 't'
  );
}

export function isMassFlowInputUnit(inputUnit: string): boolean {
  return isMassFlowQuantityUnit(quantityUnitFromInputUnit(inputUnit));
}
